/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import { styled } from '@mui/material/styles';
import GUIProgress from "./GUIProgress";
import GUILogo from "./GUILogo";
import Chip from '@mui/material/Chip';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import NavLinkAdapter from "../../../../../../@BASE/core/NavLinkAdapter";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

const GUICount = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(#0056c9,#014db3)',
  color: '#fff',
  padding: '5px 6px',
  fontSize: 10,
  fontWeight: '700',
  borderRadius: 4,
  height: 20,
  display: 'inline-block',
  lineHeight: '10px',
  width: 'fit-content',
  marginTop: 4,
  '& .MuiChip-label': {
    paddingLeft: 0,
    paddingRight: 0,
    lineHeight: '10px',
  },
}));

const GUIButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  background: 'linear-gradient(#2c2c2c,#242424)',
  borderRadius: '50% !important',
  border: 'none !important',
  borderTop: '1px solid rgba(255,255,255,.15) !important',
  width: 44,
  height: 44,
  maxWidth: 44,
  maxHeight: 44,
  margin: '3px 9px',
  padding: 0,
  zIndex: 1,
  '&::before, &::after': {
    content: '""',
    display: 'block',
    width: 50,
    height: 51,
    position: 'absolute',
    borderRadius: '50% !important',
    zIndex: -1,
  },
  '&::before': {
    border: '3px solid #000',
  },
  '&::after': {
    borderBottom: '1px solid rgba(255,255,255,.25)',
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

function GUIFooter(props) {
  // const { pages, currentPage } = props;
  const { currentPage, instance } = props;
  // update the current page data by matching the page id from pages array
  // const page = pages.find((page) => page.id === currentPage.id);
  // const pageIndex = pages.indexOf(page) + 1;
  const page = instance
  const pageIndex = 1;
  // popover
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <div 
      className="flex flex-col w-full h-88 text-white mt-0 mb-16"
      style={{
        borderBottom: '1px inset rgba(0,0,0,.15)',
        overflow: 'hidden',
      }}
    >
      {/* <GUIProgress pages={pages} currentPage={currentPage} /> */}
      <div
        className="flex items-center px-16 h-full"
        style={{
          background: 'linear-gradient(#282828,#181818)',
          borderTop: '1px solid rgba(255,255,255,.15)',
        }}
      >
        <div 
          className="flex"
          style={{
            width: 'calc(50% - 93px)',
          }}
        >
          <GUILogo />
          <div className="flex flex-col">
            <p
              style={{
                fontSize: "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 300,
              }}
            >
              {currentPage.section}
            </p>
            <p
              style={{
                fontSize: "16px",
                fontWeight: "700",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 300,
              }}
            >
              {currentPage.title}
            </p>
            {/* <GUICount label={`${pageIndex} OF ${pages.length}`} /> */}
            <GUICount label={`${pageIndex} OF 1`} />
          </div>
        </div>
        <div className="shrink">
          <ButtonGroup
            disableElevation
            variant="contained"
            aria-label="Main Navigation Controls"
          >
            <GUIButton
              component={NavLinkAdapter}
              to={page?.prev_id != null ? `/pages/${page.prev_id}/live-preview` : ''}
              disabled={page?.prev_id == null}
            >
              <NavigateBeforeOutlinedIcon />
            </GUIButton>
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <MenuOutlinedIcon />
            </GUIButton>
            <GUIButton
              component={NavLinkAdapter}
              to={page?.next_id != null ? `/pages/${page.next_id}/live-preview` : ''}
              disabled={page?.next_id == null}
            >
              <NavigateNextOutlinedIcon />
            </GUIButton>
          </ButtonGroup>
        </div>
        <div 
          className="text-right"
          style={{
            width: 'calc(50% - 93px)',
          }}
        >
          <ButtonGroup
            disableElevation
            variant="contained"
            aria-label="Main Navigation Controls"
          >
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <PersonOutlineOutlinedIcon />
            </GUIButton>
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <BookmarkBorderOutlinedIcon />
            </GUIButton>
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                focusable="false"
                viewBox="0 0 64 64"
                style={{ width: 18, height: 18, fill: '#fff', display: 'inline-block', transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms', fontSize: '2.4rem'}}
              >
                <path
                  className="glossary-icon"
                  d="M63.8 58.3c-1.4-0.5-2.8-0.9-4-1 -1.2-0.1-2.1-0.4-2.7-0.9 -0.3-0.2-0.8-0.9-1.3-2.2 -1.1-2.1-1.9-4-2.5-5.7 -1-2.7-1.8-4.8-2.5-6.3 -1.5-3.4-2.7-6.4-3.7-9 -0.5-1.4-2.1-5.2-4.8-11.4L34.5 3.3l-0.4-0.8h-2 -2.9L18.3 30.3 9.2 54c-0.7 1.3-1.3 2.2-1.8 2.6 -0.5 0.4-1.9 0.8-4.2 1.2 -1.5 0.3-2.5 0.5-3.1 0.7L0 61.5h0.8c2.2 0 4.3-0.2 6.4-0.5l1.8-0.3c7.3 0.5 11.7 0.8 13.4 0.8 0.1-0.3 0.1-0.6 0.1-1 0-0.8 0-1.5 0-2.2 -0.5-0.1-1.1-0.2-1.6-0.2 -0.5-0.1-0.9-0.1-1.2-0.2 -0.3-0.1-0.4-0.1-0.5-0.1 -2-0.3-3.3-0.7-4-1.2 -0.4-0.3-0.7-0.9-0.7-1.7 0-0.5 0.3-1.6 0.9-3.2 1.9-5.4 3.3-8.9 4-10.5l17.3 0.1 3.5 8.8 1.8 4.4c0.2 0.4 0.2 0.8 0.2 1.2s-0.2 0.7-0.5 1c-0.3 0.3-1.2 0.6-2.6 0.9l-5 1.1c-0.1 0.8-0.2 1.8-0.2 3l2.2-0.1 7.7-0.4c1.4-0.1 3.2-0.1 5.2-0.1 2.5 0 4.9 0.1 7.3 0.3 2.9 0.2 5.3 0.3 7.4 0.3C64 61.2 64 60.9 64 60.5 64 60.1 63.9 59.3 63.8 58.3zM33.8 36.5c-0.4 0-1 0-2 0 -1 0-2.5 0-4.6-0.1 -2.1 0-4-0.1-5.9-0.1l6.5-17.3c1 2 2.2 4.7 3.5 8.1 1.5 3.7 2.7 6.8 3.5 9.3L33.8 36.5z"
                />
              </svg>
            </GUIButton>
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <QuestionMarkOutlinedIcon />
            </GUIButton>
            <GUIButton
              aria-describedby={id}
              onClick={handleClick}
            >
              <PrintOutlinedIcon />
            </GUIButton>
          </ButtonGroup>
        </div>
      </div>
      <Popover 
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Typography sx={{ p: 2 }}>Action Disabled</Typography>
      </Popover>
    </div>
  );
}

export default GUIFooter;